/**
 * Browser GA4 adapter.
 *
 * The adapter loads gtag.js only after a valid Measurement ID is supplied.
 * Every public method is fail-safe so analytics can never interrupt the app.
 */
import { storage } from '../platform/storage';
import { ANALYTICS_EVENTS } from './events';
import type { AnalyticsEventName, AnalyticsProps } from './events';
import type {
  AnalyticsClient,
  AnalyticsInitConfig,
  WebAnalyticsEventName,
} from './types';

type Gtag = (...args: unknown[]) => void;

type AnalyticsWindow = Window & {
  dataLayer?: unknown[];
  gtag?: Gtag;
};

const GA4_SCRIPT_ID = 'wlb-ga4-script';
const GA4_ID_PATTERN = /^G-[A-Z0-9]{6,20}$/;

const ALLOWED_PARAM_KEYS = new Set([
  'debug_mode',
  'environment',
  'method',
  'page_location',
  'page_name',
  'page_path',
  'page_referrer',
  'page_title',
  'post_type',
  'source',
]);

let measurementId = '';
let environment: 'dev' | 'prod' = 'dev';
let debug = false;
let enabled = false;
let currentPageLocation = '';
let currentPageReferrer = '';

export const isValidGa4MeasurementId = (value: unknown): value is string =>
  typeof value === 'string' &&
  GA4_ID_PATTERN.test(value.trim()) &&
  !/^G-X+$/i.test(value.trim());

export const toGa4Environment = (
  appEnv: string | null | undefined,
): 'dev' | 'prod' => {
  const normalized = (appEnv ?? '').trim().toLowerCase();
  return normalized === 'production' || normalized === 'prod' ? 'prod' : 'dev';
};

export const sanitizeGa4Params = (
  properties: AnalyticsProps | undefined,
  appEnvironment: 'dev' | 'prod',
  isDebug: boolean,
): Record<string, string | number | boolean> => {
  const safe: Record<string, string | number | boolean> = {
    environment: appEnvironment,
  };
  if (isDebug) {
    safe.debug_mode = true;
  }

  Object.entries(properties ?? {}).forEach(([key, value]) => {
    if (
      ALLOWED_PARAM_KEYS.has(key) &&
      value !== null &&
      value !== undefined &&
      (typeof value === 'string' ||
        typeof value === 'number' ||
        typeof value === 'boolean')
    ) {
      safe[key] = value;
    }
  });
  return safe;
};

export const resolveWebAnalyticsEventName = (
  event: AnalyticsEventName,
): string | null => {
  switch (event) {
    case ANALYTICS_EVENTS.REGISTRATION_COMPLETED:
      return 'sign_up';
    case ANALYTICS_EVENTS.LOGIN_COMPLETED:
      return 'login';
    case ANALYTICS_EVENTS.ACTIVATION_FIRST_POST_CREATED:
      return 'first_post_created';
    default:
      return null;
  }
};

const getAnalyticsWindow = (): AnalyticsWindow | null =>
  typeof window === 'undefined' ? null : (window as AnalyticsWindow);

const logDev = (message: string, details?: unknown): void => {
  if (!debug) return;
  if (details === undefined) {
    console.info(`[GA4] ${message}`);
  } else {
    console.info(`[GA4] ${message}`, details);
  }
};

const ensureGtag = (): Gtag | null => {
  const analyticsWindow = getAnalyticsWindow();
  if (!analyticsWindow) return null;

  analyticsWindow.dataLayer = analyticsWindow.dataLayer ?? [];
  analyticsWindow.gtag =
    analyticsWindow.gtag ??
    (function gtag(..._args: unknown[]) {
      // Google’s loader expects the same array-like `arguments` objects used by
      // its standard installation snippet, not ordinary nested arrays.
      analyticsWindow.dataLayer!.push(arguments);
    } as Gtag);
  return analyticsWindow.gtag;
};

const toSafePageLocation = (pagePath: string): string => {
  const pathname = pagePath.split(/[?#]/, 1)[0] || '/';
  if (typeof window === 'undefined') return pathname;
  const normalized = pathname.startsWith('/') ? pathname : `/${pathname}`;
  return `${window.location.origin}${normalized}`;
};

const getDefaultUrlContext = (): Record<string, string> => ({
  ...(currentPageLocation ? { page_location: currentPageLocation } : {}),
  ...(currentPageReferrer ? { page_referrer: currentPageReferrer } : {}),
});

const setDefaultUrlContext = (gtag: Gtag): void => {
  const context = getDefaultUrlContext();
  if (Object.keys(context).length > 0) {
    gtag('set', context);
  }
};

const loadGtagScript = (): void => {
  if (typeof document === 'undefined' || document.getElementById(GA4_SCRIPT_ID)) {
    return;
  }
  const script = document.createElement('script');
  script.id = GA4_SCRIPT_ID;
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(
    measurementId,
  )}`;
  document.head.appendChild(script);
};

const dispatchEvent = (
  event: string,
  properties?: AnalyticsProps,
  urlContext?: Record<string, string>,
): boolean => {
  if (!enabled || !event) return false;
  try {
    const gtag = ensureGtag();
    if (!gtag) return false;
    const params = {
      ...sanitizeGa4Params(properties, environment, debug),
      ...getDefaultUrlContext(),
      ...urlContext,
    };
    gtag('event', event, params);
    logDev(`event: ${event}`, params);
    return true;
  } catch (error) {
    logDev(`event failed: ${event}`, error);
    return false;
  }
};

const init = async (config: AnalyticsInitConfig): Promise<void> => {
  try {
    const candidate = config.ga4MeasurementId?.trim() ?? '';
    debug = config.isDebug === true;
    environment = toGa4Environment(config.appEnv);
    if (!isValidGa4MeasurementId(candidate)) {
      enabled = false;
      measurementId = '';
      logDev('disabled: VITE_GA4_MEASUREMENT_ID is missing or invalid');
      return;
    }

    measurementId = candidate;
    enabled = true;
    currentPageLocation = toSafePageLocation(config.initialPagePath ?? '/');
    currentPageReferrer = config.initialPageReferrer ?? '';
    const gtag = ensureGtag();
    if (!gtag) {
      enabled = false;
      return;
    }
    loadGtagScript();
    gtag('js', new Date());
    setDefaultUrlContext(gtag);
    // React Router owns page views, so automatic history tracking is disabled.
    gtag('config', measurementId, {
      send_page_view: false,
      debug_mode: debug || undefined,
      ...getDefaultUrlContext(),
    });
    logDev('initialized', { environment, measurementId });
  } catch (error) {
    enabled = false;
    logDev('initialization failed', error);
  }
};

export const analyticsClient: AnalyticsClient = {
  init,
  // GA4 receives no user IDs or traits in this implementation.
  identify: () => {},
  reset: () => {},
  track: (event, properties) => {
    const ga4Event = resolveWebAnalyticsEventName(event);
    if (ga4Event) {
      dispatchEvent(ga4Event, properties);
    }
  },
  trackEvent: (event: WebAnalyticsEventName, properties?: AnalyticsProps) => {
    dispatchEvent(event, properties);
  },
  trackPageView: (pagePath, properties) => {
    const nextPageLocation = toSafePageLocation(pagePath);
    const pageReferrer =
      nextPageLocation === currentPageLocation
        ? currentPageReferrer
        : currentPageLocation;
    const tracked = dispatchEvent('page_view', {
      ...properties,
      page_path: pagePath,
      page_title: typeof document === 'undefined' ? undefined : document.title,
    }, {
      page_location: nextPageLocation,
      ...(pageReferrer ? { page_referrer: pageReferrer } : {}),
    });
    if (tracked) {
      currentPageLocation = nextPageLocation;
      currentPageReferrer = pageReferrer;
      const gtag = ensureGtag();
      if (gtag) setDefaultUrlContext(gtag);
    }
  },
  trackOnce: async (storageKey, event, properties) => {
    try {
      const alreadyTracked = await storage.getItem<boolean>(storageKey);
      if (alreadyTracked) {
        logDev(`once skipped: ${event}`);
        return;
      }
      const ga4Event = resolveWebAnalyticsEventName(event);
      if (ga4Event && dispatchEvent(ga4Event, properties)) {
        await storage.setItem(storageKey, true);
      }
    } catch (error) {
      logDev(`once failed: ${event}`, error);
    }
  },
  screen: (screenName, properties) => {
    analyticsClient.trackPageView(screenName, {
      ...properties,
      page_name: screenName,
    });
  },
  setAttribution: () => {},
  setBranchAttribution: () => {},
};
